'use client'

import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslations } from 'next-intl'

import {
  AlertBox,
  AlertBoxType,
  Button,
  Heading,
  InputHelperText,
  Select,
  type SelectItem,
  TextArea,
} from '@graftcode/design-system'
import { zodResolver } from '@hookform/resolvers/zod'

import { useToastStore } from '@common/core/stores'

import { submitFeedback } from '../actions/submitFeedback'

import { FEEDBACK_TOPIC_OPTIONS } from './const/feedback-topics.const'
import { type CreateFeedbackFormData, createFeedbackSchema } from './Create.schema'
import type { Props } from './Create.types'
import { MoodPicker } from './MoodPicker'

import styles from './Create.module.scss'

export function CreateFeedbackForm({
  onSuccess,
  onSubmittingChange,
  hideTitle = false,
  selectContentClassName,
}: Props) {
  // Hooks
  const t = useTranslations('feedback')
  const showToast = useToastStore((state) => state.showToast)

  // Form
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isValid, isDirty },
    setError,
  } = useForm<CreateFeedbackFormData>({
    resolver: zodResolver(createFeedbackSchema((key) => t(`create.${key}`))),
    mode: 'onChange',
    defaultValues: {
      message: '',
    },
  })

  useEffect(() => {
    onSubmittingChange?.(isSubmitting)
  }, [isSubmitting, onSubmittingChange])

  const topicOptions: SelectItem[] = useMemo(
    () =>
      FEEDBACK_TOPIC_OPTIONS.map<SelectItem>((option) => ({
        type: 'item',
        value: option.value,
        label: t(`create.${option.labelKey}`),
      })),
    [t]
  )

  const onSubmit = async (data: CreateFeedbackFormData) => {
    try {
      await submitFeedback({
        topic: data.topic,
        message: data.message,
        rating: data.mood,
      })
      onSuccess?.()
    } catch {
      setError('root', {
        type: 'manual',
        message: t('create.errors.unexpectedError'),
      })
      showToast({
        variant: 'error',
        title: t('create.errors.alertTitle'),
        description: t('create.errors.unexpectedError'),
      })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      {!hideTitle && (
        <div className={styles.header}>
          <Heading type="h2" className={styles.title}>
            {t('create.title')}
          </Heading>
        </div>
      )}

      <div className={styles.fields}>
        <div className={styles.field}>
          <Controller
            name="topic"
            control={control}
            render={({ field }) => (
              <Select
                size="md"
                variant={errors.topic ? 'error' : 'default'}
                placeholder={t('create.topicPlaceholder')}
                aria-label={t('create.topicLabel')}
                options={topicOptions}
                value={field.value ?? ''}
                disabled={isSubmitting}
                contentClassName={selectContentClassName}
                onValueChange={field.onChange}
              />
            )}
          />
          {errors.topic?.message && <InputHelperText variant="error">{errors.topic.message}</InputHelperText>}
        </div>

        <div className={styles.field}>
          <TextArea
            id="feedback-message"
            size="md"
            variant={errors.message ? 'error' : 'default'}
            placeholder={t('create.messagePlaceholder')}
            aria-label={t('create.messageLabel')}
            disabled={isSubmitting}
            rows={6}
            {...register('message')}
          />
          {(errors.message?.message || errors.mood?.message) && (
            <InputHelperText variant="error">{errors.message?.message ?? errors.mood?.message}</InputHelperText>
          )}
        </div>
      </div>

      {errors.root?.message && (
        <div className={styles.formError}>
          <AlertBox
            size="sm"
            className={styles.alertBox}
            title={t('create.errors.alertTitle')}
            description={errors.root.message}
            type={AlertBoxType.Critical}
            showCloseButton={false}
          />
        </div>
      )}

      <div className={styles.actions}>
        <Controller
          name="mood"
          control={control}
          render={({ field }) => (
            <MoodPicker value={field.value ?? null} onChange={field.onChange} disabled={isSubmitting} />
          )}
        />

        <Button type="submit" variant="primary" size="l" disabled={(!isDirty && !isValid) || isSubmitting}>
          {isSubmitting ? t('create.submitting') : t('create.submitButton')}
        </Button>
      </div>
    </form>
  )
}
